import { CommonModule } from '@angular/common'
import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    Output
} from '@angular/core'
import { RouterModule } from '@angular/router'

import { formatDurationMinutes, formatDurationMinutesHM } from 'src/app/utils/format-duration'
import { varianceClassFromRatio } from '../formatters/detail-formatters'
import {
    BatchStationEntryRow,
    BatchStationVarianceRow
} from '../models/detail-page.model'
import { RowExpanderComponent } from '../row-expander/row-expander.component'
import { VariancePillComponent } from '../variance-pill/variance-pill.component'

const NULL_KEY = '__null__'

interface BarLayout {
    fillPct: number
    markerPct: number
}

/**
 * Per-station-run efficiency table for a single batch (one job).
 *
 * Renders Labor / Dwell / Standard / Labor-vs-Standard mini-bar / Effectiveness% /
 * Operators per station run, with an expander row that reveals labor entries when
 * the consumer wires `entriesByStation`. Click events are emitted, not
 * handled — the consumer owns modal opens, navigation, etc.
 *
 * The touch-bar fill coloring still uses `varianceClass` (over/under/ok by
 * the raw ratio) — it's a visual gradient where "over" = bar past standard
 * marker, regardless of efficiency framing. The pill renders efficiency %.
 *
 * The `data-testid` contract here is the architect-acceptance contract for
 * PR #3089. Any rename here breaks `improve-pr-3089-followup.spec.ts` and
 * `batch-focus-page.spec.ts`. Do not change without updating both specs.
 */
@Component({
    selector: 'app-station-variance-table-batch',
    standalone: true,
    imports: [CommonModule, RouterModule, VariancePillComponent, RowExpanderComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './station-variance-table-batch.component.html',
    styleUrls: ['./station-variance-table-batch.component.scss']
})
export class StationVarianceTableBatchComponent {
    @Input() rows: BatchStationVarianceRow[] = []
    @Input() entriesByStation: Map<string, BatchStationEntryRow[]> = new Map()
    @Input() expandedStations: Set<string> = new Set()
    @Input() operatorRouteFn: ((userId: string) => string[] | null) | null = null
    @Input() testId = 'variance-table'

    @Output() stationToggled = new EventEmitter<BatchStationVarianceRow>()
    @Output() entryEditClicked = new EventEmitter<BatchStationEntryRow>()

    readonly formatMinutes = formatDurationMinutes
    readonly formatMinutesHM = formatDurationMinutesHM
    readonly varianceClass = varianceClassFromRatio

    get showsReworkBreakdown(): boolean {
        return this.rows.some(row =>
            row.first_pass_touch_minutes != null || row.rework_touch_minutes != null
        )
    }

    get columnCount(): number {
        return this.showsReworkBreakdown ? 10 : 8
    }

    rowKey(row: BatchStationVarianceRow): string {
        return row.row_id ?? row.station_id ?? NULL_KEY
    }

    isExpanded(row: BatchStationVarianceRow): boolean {
        return this.expandedStations.has(this.rowKey(row))
    }

    entriesFor(row: BatchStationVarianceRow): BatchStationEntryRow[] {
        return this.entriesByStation.get(this.rowKey(row)) ?? []
    }

    operatorLink(userId: string | null): string[] | null {
        if (!userId || !this.operatorRouteFn) return null
        return this.operatorRouteFn(userId)
    }

    touchBarLayout(row: BatchStationVarianceRow): BarLayout {
        const standard = row.standard_minutes ?? 0
        if (!standard) return { fillPct: 0, markerPct: 0 }
        const touch = Math.max(row.touch_minutes ?? 0, 0)
        const scale = Math.max(touch, standard)
        return {
            fillPct: scale > 0 ? (touch / scale) * 100 : 0,
            markerPct: scale > 0 ? (standard / scale) * 100 : 0
        }
    }

    onToggle(row: BatchStationVarianceRow): void {
        this.stationToggled.emit(row)
    }

    onEntryEdit(entry: BatchStationEntryRow): void {
        this.entryEditClicked.emit(entry)
    }

    entriesRowId(row: BatchStationVarianceRow): string {
        return 'variance-entries-' + this.rowKey(row)
    }
}
